import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Newspaper, ExternalLink, ArrowRight, Clock, RefreshCw, AlertCircle, MapPin } from 'lucide-react';
import { getNewsArticles, getEvents } from '../utils/api';

// Helper function to decode HTML entities and clean text
const decodeHtmlEntities = (text) => {
  if (!text) return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  let decoded = textarea.value;
  // Also handle common HTML entities that might not be decoded
  decoded = decoded
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#8230;/g, '…')
    .replace(/&#8217;/g, "'")
    .replace(/&#8216;/g, "'")
    .replace(/&#8220;/g, '"')
    .replace(/&#8221;/g, '"')
    .replace(/&#8211;/g, '–')
    .replace(/&#8212;/g, '—')
    .replace(/&nbsp;/g, ' ')
    .replace(/&#160;/g, ' ')
    .replace(/<[^>]*>/g, ''); // Remove any HTML tags
  return decoded;
};

// Helper function to render text with styled "Lex" brand
const renderStyledText = (text) => {
  const parts = text.split(/(Lex(?:DoRight|Procure|Delta|Agenda)?)/g);
  return parts.map((part, index) => {
    if (part.startsWith('Lex')) {
      const rest = part.slice(3);
      return (
        <span key={index}>
          <span className="text-[#8B1818] font-semibold">Lex</span>{rest}
        </span>
      );
    }
    return part;
  });
};

const Newsroom = () => {
  const [activeTab, setActiveTab] = useState('news');
  const [newsArticles, setNewsArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getNewsArticles(20);
      if (response.data && response.data.articles) {
        setNewsArticles(response.data.articles);
        setLastUpdated(response.data.last_updated);
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Unable to fetch latest news. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    setEventsLoading(true);
    try {
      const response = await getEvents(false); // Only published events
      setEvents(response.data || []);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
    fetchEvents();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="py-12 bg-[#F9F9F7]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            Newsroom
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Stay updated with {renderStyledText('LexDoRight')} events and the latest news on Philippine laws and technology
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Tabs */}
        <div className="flex border-b border-slate-200 mb-8">
          <button
            onClick={() => setActiveTab('news')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'news'
                ? 'text-[#8B1818] border-b-2 border-[#8B1818]'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Newspaper className="inline h-5 w-5 mr-2" />
            Law & Tech News
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'events'
                ? 'text-[#8B1818] border-b-2 border-[#8B1818]'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Calendar className="inline h-5 w-5 mr-2" />
            {renderStyledText('LexDoRight')} Events
          </button>
        </div>

        {/* News Tab */}
        {activeTab === 'news' && (
          <div>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg mb-8">
              <div className="flex justify-between items-center">
                <p className="text-sm text-amber-900">
                  <strong>Daily News Digest:</strong> Curated news on Philippine laws, regulations, and technology developments. 
                  Updated regularly from trusted sources.
                </p>
                <button
                  onClick={fetchNews}
                  disabled={loading}
                  className="ml-4 p-2 text-amber-700 hover:text-amber-900 transition-colors disabled:opacity-50"
                  title="Refresh news"
                >
                  <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              {lastUpdated && (
                <p className="text-xs text-amber-700 mt-2">
                  Last updated: {formatDate(lastUpdated)}
                </p>
              )}
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-12">
                <RefreshCw className="h-8 w-8 text-[#8B1818] animate-spin" />
                <span className="ml-3 text-slate-600">Loading latest news...</span>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-8">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                  <p className="text-sm text-red-900">{error}</p>
                </div>
                <button
                  onClick={fetchNews}
                  className="mt-2 text-sm text-red-700 hover:text-red-900 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {/* News Articles */}
            {!loading && !error && (
              <>
                {newsArticles.length === 0 ? (
                  <div className="text-center py-12">
                    <Newspaper className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No news articles available at the moment.</p>
                    <button
                      onClick={fetchNews}
                      className="mt-4 text-[#8B1818] hover:underline"
                    >
                      Refresh
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {newsArticles.map((article) => (
                      <div key={article.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-xs text-[#8B1818] font-medium mb-2">{article.source}</p>
                            <h3 className="text-xl font-bold text-slate-900 mb-2">{decodeHtmlEntities(article.title)}</h3>
                            <p className="text-slate-600 mb-3">{decodeHtmlEntities(article.excerpt)}</p>
                            <div className="flex items-center text-sm text-slate-500">
                              <Clock className="h-4 w-4 mr-1" />
                              {formatDate(article.date)}
                            </div>
                          </div>
                          {article.url && article.url !== '#' && (
                            <a 
                              href={article.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="ml-4 p-2 text-slate-400 hover:text-[#8B1818] transition-colors"
                              title="Read full article"
                            >
                              <ExternalLink className="h-5 w-5" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="mt-8 text-center">
              <p className="text-slate-500 text-sm">
                News articles are sourced from reputable Philippine media outlets. 
                {renderStyledText('LexDoRight')} does not own or create this content.
              </p>
            </div>
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div>
            {eventsLoading ? (
              <div className="flex justify-center items-center py-12">
                <RefreshCw className="h-8 w-8 text-[#8B1818] animate-spin" />
                <span className="ml-3 text-slate-600">Loading events...</span>
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="h-16 w-16 text-slate-300 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-slate-900 mb-3">No Upcoming Events</h3>
                <p className="text-slate-600 max-w-md mx-auto mb-6">
                  We are currently planning exciting events for {renderStyledText('LexDoRight')}. 
                  Check back soon for updates on workshops, webinars, and conferences.
                </p>
                <Link to="/contact" className="inline-flex items-center text-[#8B1818] font-medium hover:underline">
                  Get Notified About Future Events <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            ) : (
              <div className="grid gap-6">
                {events.map((event) => (
                  <div key={event.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="flex">
                      {/* Date Badge */}
                      <div className="bg-[#8B1818] text-white p-6 flex flex-col items-center justify-center min-w-[120px]">
                        <span className="text-3xl font-bold">{new Date(event.date).getDate()}</span>
                        <span className="text-sm uppercase">{new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                        <span className="text-xs mt-1">{new Date(event.date).getFullYear()}</span>
                      </div>
                      {/* Event Details */}
                      <div className="p-6 flex-1">
                        <h3 className="text-xl font-bold text-slate-900 mb-2">{renderStyledText(event.title)}</h3>
                        <p className="text-slate-600 mb-4">{renderStyledText(event.description)}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                          <span className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {event.time}
                          </span>
                          <span className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1" />
                            {event.location}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8 bg-slate-100 rounded-xl p-8 text-center">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Want to Partner with Us?</h3>
              <p className="text-slate-600 mb-4">
                Interested in hosting an event or partnering with {renderStyledText('LexDoRight')} for legal tech initiatives?
              </p>
              <Link to="/contact" className="inline-flex items-center text-[#8B1818] font-medium hover:underline">
                Get in Touch <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Newsroom;
